<template>
  <v-container fluid>
    <v-card>
      <v-card-text>
        <v-form
          ref="form"
          id="printerSettingForm"
          class="printer-setting-form"
        >
          <v-row>
            <v-col
              cols="2"
              md="2"
            >
              <BinaryToggle
                class="printer-setting-printer-enable-switch-btn"
                id="printerSettingPrinterEnableSwitchBtn"
                aria-label="Printer: enabled or disabled"
                off-label="Disabled"
                on-label="Enabled"
                v-model="editItem.isPrinterEnabled" />
            </v-col>
            <v-col
              cols="2"
              md="2"
            >
              <v-text-field
                class="printer-setting-printer-port-text-field"
                id="printerSettingPrinterPortTextField"
                v-model="editItem.printerPort"
                label="Printer Port"
                hint="Numbers only. Leave this at 3010 unless we ask you to change it."
              ></v-text-field>
            </v-col>
            <!-- <v-col
              cols="2"
              md="2"
            >
             <v-select
                class="printer-setting-printer-select"
                id="printerSettingPrinterSelect"
                v-model="editItem.printer"
                :items="printerList"
                item-text="name"
                item-value="deviceId"
                label="Printer"
              ></v-select>
            </v-col> -->
          </v-row>
          <v-row v-if="editItem.isPrinterEnabled">
            <v-col cols="12">
              <div class="text-subtitle-2">Where each document prints</div>
              <div class="text-caption printer-setting-help">
                Choose a printer, or Preview, for each document on the Printer tab of
                RegovCountyToolkit on this PC. New documents appear there by themselves.
              </div>
              <div
                v-if="toolkitStatus"
                class="text-body-2 mt-2 printer-setting-toolkit-status"
                id="printerSettingToolkitStatus"
              >{{ toolkitStatus }}</div>
            </v-col>
          </v-row>
        </v-form>

      <v-card-actions>
        <v-btn
          class="printer-setting-test-toolkit-btn"
          id="printerSettingTestToolkitBtn"
          depressed
          color="primary"
          :disabled="!isLoadConfig || testing"
          @click="testToolkit"
        >
          TEST CONNECTION
        </v-btn>
      </v-card-actions>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import BinaryToggle from '../base/BinaryToggle.vue';
import {
  getPrinterConfig, getPrinterList, setPrinterConfig, DEFAULT_PRINTER_PORT,
  testToolkitConnection,
} from '../../../util/pdf-to-printer.util';

export default {
  name: 'WorkstationSetting',
  components: { BinaryToggle },
  data: () => ({
    editItem: {
      isPrinterEnabled: false,
      // Pre-filled rather than blank: the print helper's port is fixed at 3010
      // and nothing on this screen used to tell the county that, so a blank box
      // was left blank and printing silently went nowhere.
      printerPort: DEFAULT_PRINTER_PORT,
      printer: null,
    },
    printerList: [],
    toolkitStatus: '',
    testing: false,
    saveTimeout: null,
    // Set while loadData() is populating editItem; see debouncedSave().
    isLoading: false,
  }),
  mounted() {
    this.loadData();
    // this.loadPrinterList();
  },
  // debouncedSave() leaves a 1s timer behind. Without this, leaving the screen
  // within a second of changing anything fires save() on a destroyed component,
  // where this.$refs.form is gone - "Cannot read properties of undefined
  // (reading 'validate')", thrown from a timer callback so nothing catches it.
  // The pending change is lost either way; this stops it taking an exception
  // with it. Surfaced by PrinterSetting.vue.spec.js, which aborted the whole
  // unit run until this was added.
  beforeDestroy() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  },
  watch: {
    'editItem.isPrinterEnabled': {
      handler() {
        this.debouncedSave();
      },
    },
    'editItem.printerPort': {
      handler() {
        this.debouncedSave();
      },
    },
    'editItem.printer': {
      handler() {
        this.debouncedSave();
      },
    },
  },
  computed: {
    isLoadConfig() {
      return this.editItem && this.editItem.isPrinterEnabled && this.editItem.printerPort;
    },
  },
  methods: {
    ...mapActions(['flashSuccess', 'flashError']),
    loadData() {
      // Assigning editItem trips the printerPort and isPrinterEnabled watchers,
      // so without this flag merely opening the screen queues a save and flashes
      // "Printer settings saved" at someone who changed nothing. Vue flushes
      // watcher callbacks before $nextTick callbacks, so the flag is still set
      // by the time they run.
      this.isLoading = true;
      const data = getPrinterConfig();
      this.editItem = { ...this.editItem, ...data };
      // A previously saved blank port would otherwise clear the default back out.
      if (!this.editItem.printerPort) {
        this.editItem.printerPort = DEFAULT_PRINTER_PORT;
      }
      this.$nextTick(() => { this.isLoading = false; });
    },
    async loadPrinterList() {
      const printerList = await getPrinterList();
      this.printerList = printerList;
    },
    debouncedSave() {
      // Populating the form is not the user editing it.
      if (this.isLoading) return;
      // Clear existing timeout
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }

      // Set new timeout for debounced save
      this.saveTimeout = setTimeout(() => {
        this.save();
      }, 1000); // 1 second debounce
    },
    save() {
      // There is no Save button on this screen - it debounce-saves. A failed
      // validate() used to drop the write silently, leaving a switch sitting
      // in its new position with nothing persisted and nothing said.
      if (!this.$refs.form.validate()) {
        this.flashError('Printer settings not saved - check the fields above.');
        return;
      }
      const printerConfig = { ...this.editItem };
      setPrinterConfig(printerConfig);
      this.flashSuccess('Printer settings saved');
    },
    async testToolkit() {
      this.testing = true;
      this.toolkitStatus = 'Checking for RegovCountyToolkit...';
      try {
        const result = await testToolkitConnection({ printerPort: this.editItem.printerPort });
        if (result && result.blocked) {
          this.toolkitStatus = 'Your browser blocked the test window. Allow pop-ups for this site and try again.';
        } else if (result) {
          this.toolkitStatus = `Connected to RegovCountyToolkit ${result.version}.`;
        } else {
          this.toolkitStatus = 'No answer from RegovCountyToolkit on this port. It may not be running, or it may be an older version that prints with the Windows default printer for any document it does not know.';
        }
      } finally {
        this.testing = false;
      }
    },

  },
};
</script>
